<script setup>
import { computed, ref, reactive, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LabelActions from 'dashboard/components/widgets/conversation/conversationBulkActions/LabelActions.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  visibleContactIds: { type: Array, default: () => [] },
  selectedContactIds: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'clearSelection',
  'assignLabels',
  'toggleAll',
  'deleteSelected',
  'bulkUpdateField',
]);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const contactFieldDefs = computed(() =>
  getters['contactFieldDefinitions/getEnabled'].value || []
);

onMounted(() => store.dispatch('contactFieldDefinitions/fetchAll'));

// ── BulkSelectBar wiring ──────────────────────────────────────────────────────
const selectedCount = computed(() => props.selectedContactIds.length);
const totalVisibleContacts = computed(() => props.visibleContactIds.length);
const showLabelSelector = ref(false);

const selectAllLabel = computed(() => {
  if (!totalVisibleContacts.value) return '';
  return t('CONTACTS_BULK_ACTIONS.SELECT_ALL', { count: totalVisibleContacts.value });
});

const selectedCountLabel = computed(() =>
  t('CONTACTS_BULK_ACTIONS.SELECTED_COUNT', { count: selectedCount.value })
);

const allItems = computed(() => props.visibleContactIds.map(id => ({ id })));

const selectionModel = computed({
  get: () => new Set(props.selectedContactIds),
  set: newSet => {
    if (!props.visibleContactIds.length) { emit('toggleAll', false); return; }
    const shouldSelectAll = props.visibleContactIds.every(id => newSet.has(id));
    emit('toggleAll', shouldSelectAll);
  },
});

const emitClearSelection = () => { showLabelSelector.value = false; showUpdatePanel.value = false; emit('clearSelection'); };

const toggleLabelSelector = () => {
  if (!selectedCount.value || props.isLoading) return;
  showLabelSelector.value = !showLabelSelector.value;
  showUpdatePanel.value = false;
};

const closeLabelSelector = () => { showLabelSelector.value = false; };

const handleAssignLabels = labels => { emit('assignLabels', labels); closeLabelSelector(); };

// ── Update field panel ────────────────────────────────────────────────────────
const SYSTEM_FIELDS = [
  { key: 'name', label: 'Name', fieldType: 'text', isCustom: false },
  { key: 'email', label: 'Email', fieldType: 'text', isCustom: false },
  { key: 'phone_number', label: 'Phone', fieldType: 'text', isCustom: false },
];

const allFields = computed(() => [
  ...SYSTEM_FIELDS,
  ...contactFieldDefs.value.map(fd => ({
    key: fd.field_key,
    label: fd.label,
    fieldType: fd.field_type,
    options: fd.options || [],
    isCustom: true,
  })),
]);

const showUpdatePanel = ref(false);
const updatePanelRef = ref(null);
const updateState = reactive({ fieldKey: '', value: '', fieldType: 'text', isCustom: false, options: [] });

const toggleUpdatePanel = () => {
  if (!selectedCount.value || props.isLoading) return;
  showUpdatePanel.value = !showUpdatePanel.value;
  showLabelSelector.value = false;
  if (showUpdatePanel.value) {
    const first = allFields.value[0];
    if (first) selectUpdateField(first.key);
  }
};

const closeUpdatePanel = () => { showUpdatePanel.value = false; };

const selectUpdateField = key => {
  const field = allFields.value.find(f => f.key === key);
  if (!field) return;
  updateState.fieldKey = field.key;
  updateState.fieldType = field.fieldType;
  updateState.isCustom = field.isCustom;
  updateState.options = field.options || [];
  updateState.value = '';
};

const applyFieldUpdate = () => {
  if (!updateState.fieldKey) return;
  emit('bulkUpdateField', {
    fieldKey: updateState.fieldKey,
    value: updateState.value,
    isCustom: updateState.isCustom,
  });
  closeUpdatePanel();
};
</script>

<template>
  <div
    class="sticky top-0 z-10 bg-gradient-to-b from-n-surface-1 from-90% to-transparent pt-1 pb-2"
  >
    <BulkSelectBar
      v-model="selectionModel"
      :all-items="allItems"
      :select-all-label="selectAllLabel"
      :selected-count-label="selectedCountLabel"
      class="py-2 ltr:!pr-3 rtl:!pl-3 justify-between"
    >
      <template #secondary-actions>
        <Button
          sm
          ghost
          slate
          :label="t('CONTACTS_BULK_ACTIONS.CLEAR_SELECTION')"
          class="!px-1.5"
          @click="emitClearSelection"
        />
      </template>
      <template #actions>
        <div class="flex items-center gap-2 ml-auto">
          <!-- Assign Labels -->
          <div
            v-on-click-outside="closeLabelSelector"
            class="relative flex items-center"
          >
            <Button
              sm
              faded
              slate
              icon="i-lucide-tags"
              :label="t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
              @click="toggleLabelSelector"
            />
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <LabelActions
                v-if="showLabelSelector"
                class="[&>.triangle]:!hidden [&>div>button]:!hidden ltr:!right-0 rtl:!left-0 top-8 mt-0.5"
                @assign="handleAssignLabels"
              />
            </transition>
          </div>

          <!-- Update field -->
          <div
            v-on-click-outside="closeUpdatePanel"
            class="relative flex items-center"
          >
            <Button
              sm
              faded
              slate
              icon="i-lucide-pencil-line"
              label="Update field"
              :disabled="!selectedCount || isLoading"
              class="w-fit"
              @click="toggleUpdatePanel"
            />
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="showUpdatePanel"
                ref="updatePanelRef"
                class="absolute right-0 top-9 z-30 w-64 bg-n-solid-2 border border-n-weak rounded-lg shadow-lg p-3 flex flex-col gap-3"
              >
                <div>
                  <label class="block text-xs font-medium text-n-slate-9 mb-1">Field</label>
                  <select
                    :value="updateState.fieldKey"
                    class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                    @change="e => selectUpdateField(e.target.value)"
                  >
                    <option v-for="f in allFields" :key="f.key" :value="f.key">{{ f.label }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-xs font-medium text-n-slate-9 mb-1">New value</label>
                  <!-- List type -->
                  <select
                    v-if="updateState.fieldType === 'list'"
                    v-model="updateState.value"
                    class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  >
                    <option value="">— clear —</option>
                    <option v-for="opt in updateState.options" :key="opt" :value="opt">{{ opt }}</option>
                  </select>
                  <!-- Checkbox type -->
                  <div v-else-if="updateState.fieldType === 'checkbox'" class="flex items-center gap-2">
                    <input
                      v-model="updateState.value"
                      type="checkbox"
                      class="w-4 h-4 accent-woot-500"
                    />
                    <span class="text-sm text-n-slate-11">{{ updateState.value ? 'Yes' : 'No' }}</span>
                  </div>
                  <!-- Number type -->
                  <input
                    v-else-if="updateState.fieldType === 'number'"
                    v-model="updateState.value"
                    type="number"
                    placeholder="Enter value"
                    class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  />
                  <!-- Text / link / default -->
                  <input
                    v-else
                    v-model="updateState.value"
                    type="text"
                    placeholder="Enter value"
                    class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                    @keydown.enter.prevent="applyFieldUpdate"
                  />
                </div>
                <Button
                  sm
                  label="Apply to selected"
                  :disabled="isLoading"
                  :is-loading="isLoading"
                  @click="applyFieldUpdate"
                />
              </div>
            </transition>
          </div>

          <!-- Delete -->
          <Policy :permissions="['administrator']">
            <Button
              v-tooltip.bottom="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
              sm
              faded
              ruby
              icon="i-lucide-trash"
              :label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
              :aria-label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="!px-1.5 [&>span:nth-child(2)]:hidden"
              @click="emit('deleteSelected')"
            />
          </Policy>
        </div>
      </template>
    </BulkSelectBar>
  </div>
</template>
